// Plan 24b, Task 2.5 — Item Targeting overlay (Screen 11)
//
// Shown when the local player activates an item that requires targeting.
// Four modes: 'skillshot' | 'cone' | 'player-select' | 'self-cast'
//
// Phase A: HTML/CSS placeholder overlays. Plan 19 wires real 3D reticles.
//
// ESC closes or skips through a named function reference for clean deregistration.

import type { ScreenHandle, ScreenMountContext } from './types';
import type { BoardMountPayload } from './board-payload';
import { createButton } from '../components/button';
import type { UIComponentHandle } from '../components/types';
import { deserializeGameState } from '@shared/protocol';
import type { ClientMessage, ServerMessage } from '@shared/protocol';
import { GRAVITY_WELL_RANGE } from '@shared/game/items/gravity-well';
import { itemIconUrl } from './item-assets';
import { getInputModality } from '../../input/modality';
import { createBoardTopTimer } from './board-timer';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export type ItemTargetingPayload = BoardMountPayload & {
  itemMode: 'skillshot' | 'cone' | 'player-select' | 'self-cast' | 'path-direction';
  itemId: string;
  itemMeta: { name: string; iconKey: string; effectText: string; damageText?: string };
};

// ─────────────────────────────────────────────────────────────────────────────
// Token constants — exact values from docs/design/tokens.json
// ─────────────────────────────────────────────────────────────────────────────

/** color.ui.panel-bg-alpha = rgba(30, 35, 50, 0.82) */
const PANEL_BG_ALPHA = 'rgba(30, 35, 50, 0.82)';

/** color.brand.amber = #E8A020 */
const AMBER = '#E8A020';

/** color.text.primary = #FFFFFF */
const WHITE = '#FFFFFF';

/** color.text.secondary = #B0B8C8 */
const SECONDARY_TEXT = '#B0B8C8';

/** border-radius.lg = 12px */
const RADIUS_LG = '12px';

/** border-radius.circle = 50% */
const RADIUS_CIRCLE = '50%';

/** typography.family.body */
const BODY_FAMILY = "'Nunito Sans', 'Nunito', 'Arial Rounded MT Bold', sans-serif";

/** typography.size.h5 = 18px */
const H5_SIZE = '18px';

/** typography.size.caption = 14px */
const CAPTION_SIZE = '14px';

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createItemTargetingScreen(
  ctx: ScreenMountContext<ItemTargetingPayload>,
): ScreenHandle {
  const { gsapRegistry, host, router, payload } = ctx;
  const { itemMode, itemId, itemMeta, state, localPlayerId } = payload;

  let disposed = false;
  let choiceMade = false;
  let actionControlsLocked = payload.isActionControlLocked?.() ?? false;
  let unsubscribe: (() => void) | null = null;
  let unsubscribeActionLock: (() => void) | null = null;
  let playerTargetingDispose: (() => void) | null = null;
  let directionTargetingDispose: (() => void) | null = null;
  let rangePreviewDispose: (() => void) | null = null;
  let playerTargetFrameId: number | null = null;
  let selectedPlayerTargetId: string | null = null;
  let selectedDirection = { dx: 1, dy: 0 };
  let selectedTargetNodeId: string | null = null;
  let playerTargetIndicator: HTMLElement | null = null;
  let playerTargetLabel: HTMLElement | null = null;
  let directionTargetLabel: HTMLElement | null = null;
  const ownsItem = state.players.get(localPlayerId)?.items.includes(itemId) === true;
  const isWarmupItemUse = payload.itemUseContext === 'warmup-arena';
  const canUseItem =
    payload.inventoryMode === 'use' &&
    ownsItem &&
    (isWarmupItemUse ||
      (state.phase === 'item_phase' &&
        state.turnOrder[state.currentPlayerIndex] === localPlayerId));

  /** Track all UIComponentHandles from createButton for disposal */
  const buttonHandles: UIComponentHandle[] = [];

  if (canUseItem) {
    payload.previewItemInHand?.(itemId);
  }

  // ── Root overlay ───────────────────────────────────────────────────────────

  const root = document.createElement('div');
  root.className = 'screen-item-targeting';
  root.setAttribute('data-screen-id', 'item-targeting');
  root.style.cssText = ['position: fixed', 'inset: 0', 'z-index: 350', 'pointer-events: none'].join(
    '; ',
  );

  const boardTimer = createBoardTopTimer(payload, { subscribe: false });
  root.appendChild(boardTimer.element);

  // ── Item info card (top-left, all modes) ──────────────────────────────────

  const infoCard = document.createElement('div');
  infoCard.className = 'item-info-card';
  infoCard.style.cssText = [
    'position: absolute',
    'top: 16px',
    'left: 16px',
    `background: ${PANEL_BG_ALPHA}`,
    `border-radius: ${RADIUS_LG}`,
    'border: 2px solid rgba(255, 198, 75, 0.65)',
    'box-shadow: 0 0 18px rgba(232,160,32,0.28), inset 0 0 0 1px rgba(255,255,255,0.08)',
    'padding: 10px 14px 10px 10px',
    'pointer-events: none',
    'user-select: none',
    `font-family: ${BODY_FAMILY}`,
    'min-width: 260px',
    'display: flex',
    'align-items: center',
    'gap: 12px',
  ].join('; ');

  const itemIconFrame = document.createElement('div');
  itemIconFrame.className = 'item-info-icon';
  itemIconFrame.style.cssText = [
    'width: 74px',
    'height: 74px',
    'border-radius: 10px',
    'overflow: hidden',
    'border: 2px solid rgba(255, 220, 110, 0.72)',
    'background: rgba(0,0,0,0.35)',
    'box-shadow: inset 0 0 12px rgba(255,220,110,0.18)',
    'flex: 0 0 auto',
  ].join('; ');

  const itemIconImg = document.createElement('img');
  itemIconImg.className = 'item-info-icon-img';
  itemIconImg.src = itemIconUrl(itemMeta.iconKey);
  itemIconImg.alt = '';
  itemIconImg.draggable = false;
  itemIconImg.style.cssText = [
    'display: block',
    'width: 100%',
    'height: 100%',
    'object-fit: cover',
  ].join('; ');
  itemIconFrame.appendChild(itemIconImg);

  const itemTextStack = document.createElement('div');
  itemTextStack.className = 'item-info-text';
  itemTextStack.style.cssText = 'display: flex; flex-direction: column; gap: 3px; min-width: 0;';

  const itemNameEl = document.createElement('div');
  itemNameEl.className = 'item-info-name';
  itemNameEl.textContent = itemMeta.name;
  itemNameEl.style.cssText = [
    `font-size: ${H5_SIZE}`,
    'font-weight: 700',
    `color: ${WHITE}`,
    'margin-bottom: 4px',
    'text-shadow: 0 2px 0 rgba(0,0,0,0.75)',
  ].join('; ');

  const itemEffectEl = document.createElement('div');
  itemEffectEl.className = 'item-info-effect';
  itemEffectEl.textContent = itemMeta.effectText;
  itemEffectEl.style.cssText = [
    `font-size: ${CAPTION_SIZE}`,
    'font-weight: 400',
    `color: ${SECONDARY_TEXT}`,
  ].join('; ');

  itemTextStack.appendChild(itemNameEl);
  itemTextStack.appendChild(itemEffectEl);

  if (itemMeta.damageText) {
    const damageEl = document.createElement('div');
    damageEl.className = 'item-info-damage';
    damageEl.textContent = itemMeta.damageText;
    damageEl.style.cssText = [
      `font-size: ${CAPTION_SIZE}`,
      'font-weight: 600',
      `color: ${WHITE}`,
      'margin-top: 2px',
    ].join('; ');
    itemTextStack.appendChild(damageEl);
  }

  infoCard.appendChild(itemIconFrame);
  infoCard.appendChild(itemTextStack);
  root.appendChild(infoCard);

  // ── Instruction bar (bottom-center, all modes) ────────────────────────────

  const instructionBar = document.createElement('div');
  instructionBar.className = 'item-instruction-bar';
  instructionBar.style.cssText = [
    'position: absolute',
    'bottom: 24px',
    'left: 50%',
    'transform: translateX(-50%)',
    `background: rgba(255, 240, 204, 0.94)`,
    `border: 3px solid rgba(72, 42, 24, 0.84)`,
    `border-radius: 10px`,
    'box-shadow: 0 4px 0 rgba(72,42,24,0.72), 0 10px 22px rgba(0,0,0,0.25)',
    'padding: 14px 28px',
    `font-family: ${BODY_FAMILY}`,
    `font-size: ${H5_SIZE}`,
    'font-weight: 800',
    `color: #3a2419`,
    'white-space: nowrap',
    'user-select: none',
    'pointer-events: none',
  ].join('; ');
  appendInstructionContent(
    instructionBar,
    itemMode,
    itemMeta.name,
    cancelChoice,
    confirmDirectionTarget,
  );

  root.appendChild(instructionBar);

  const rejectionMessage = document.createElement('div');
  rejectionMessage.className = 'item-targeting-error';
  rejectionMessage.style.cssText = [
    'position: absolute',
    'bottom: 86px',
    'left: 50%',
    'transform: translateX(-50%)',
    'max-width: min(520px, calc(100vw - 32px))',
    'padding: 10px 16px',
    'border-radius: 8px',
    'background: rgba(120, 24, 24, 0.92)',
    'border: 2px solid rgba(255, 180, 160, 0.7)',
    `font-family: ${BODY_FAMILY}`,
    `font-size: ${CAPTION_SIZE}`,
    'font-weight: 800',
    `color: ${WHITE}`,
    'text-align: center',
    'box-shadow: 0 8px 18px rgba(0,0,0,0.32)',
    'pointer-events: none',
    'display: none',
  ].join('; ');
  root.appendChild(rejectionMessage);

  const playerTargetIds =
    itemMode === 'player-select'
      ? state.turnOrder.filter(
          (playerId) => playerId !== localPlayerId && playersHas(state, playerId),
        )
      : [];

  // ── Mode-specific overlay ──────────────────────────────────────────────────

  if (!canUseItem) {
    const invalidPanel = document.createElement('div');
    invalidPanel.className = 'item-targeting-invalid-panel';
    invalidPanel.style.cssText = [
      'position: absolute',
      'top: 50%',
      'left: 50%',
      'transform: translate(-50%, -50%)',
      `background: ${PANEL_BG_ALPHA}`,
      `border-radius: ${RADIUS_LG}`,
      'border: 2px solid rgba(255, 180, 160, 0.7)',
      'padding: 20px 24px',
      'display: flex',
      'flex-direction: column',
      'align-items: center',
      'gap: 14px',
      'pointer-events: auto',
    ].join('; ');
    const invalidText = document.createElement('div');
    invalidText.textContent = 'This item cannot be used right now.';
    invalidText.style.cssText = [
      `font-family: ${BODY_FAMILY}`,
      `font-size: ${CAPTION_SIZE}`,
      'font-weight: 900',
      `color: ${WHITE}`,
      'text-align: center',
    ].join('; ');
    invalidPanel.appendChild(invalidText);
    const backBtn = createButton({
      gsapRegistry,
      variant: 'ghost',
      label: 'Back',
      onClick: cancelChoice,
    });
    buttonHandles.push(backBtn);
    invalidPanel.appendChild(backBtn.element);
    root.appendChild(invalidPanel);
  } else if (itemMode === 'skillshot') {
    // Centered concentric circles reticle
    const reticleWrapper = document.createElement('div');
    reticleWrapper.style.cssText = [
      'position: absolute',
      'inset: 0',
      'display: flex',
      'align-items: center',
      'justify-content: center',
      'pointer-events: none',
    ].join('; ');

    const reticle = document.createElement('div');
    reticle.className = 'targeting-reticle';
    reticle.style.cssText = [
      'position: relative',
      'width: 120px',
      'height: 120px',
      `border-radius: ${RADIUS_CIRCLE}`,
      `border: 6px solid rgba(255, 210, 91, 0.92)`,
      'box-shadow: 0 0 24px rgba(232,160,32,0.72), inset 0 0 18px rgba(232,160,32,0.24)',
      'display: flex',
      'align-items: center',
      'justify-content: center',
      'cursor: crosshair',
      'pointer-events: auto',
    ].join('; ');

    // Inner ring (60px)
    const innerRing = document.createElement('div');
    innerRing.className = 'targeting-reticle-inner';
    innerRing.style.cssText = [
      'width: 60px',
      'height: 60px',
      `border-radius: ${RADIUS_CIRCLE}`,
      `border: 4px solid rgba(255, 210, 91, 0.92)`,
      'pointer-events: none',
    ].join('; ');

    reticle.appendChild(innerRing);

    reticle.addEventListener('click', () => {
      confirmDirectionTarget();
    });

    reticleWrapper.appendChild(reticle);
    root.appendChild(reticleWrapper);
    mountDirectionalTargeting('skillshot');
  } else if (itemMode === 'cone') {
    // CSS clip-path triangle cone
    const coneWrapper = document.createElement('div');
    coneWrapper.style.cssText = [
      'position: absolute',
      'inset: 0',
      'display: flex',
      'align-items: center',
      'justify-content: center',
      'pointer-events: none',
    ].join('; ');

    const cone = document.createElement('div');
    cone.className = 'targeting-cone';
    cone.style.cssText = [
      'width: 160px',
      'height: 120px',
      // Clip-path triangle pointing up
      'clip-path: polygon(50% 0%, 0% 100%, 100% 100%)',
      `background: rgba(232, 160, 32, 0.3)`,
      'pointer-events: auto',
      'cursor: crosshair',
    ].join('; ');
    cone.addEventListener('click', confirmDirectionTarget);

    coneWrapper.appendChild(cone);
    root.appendChild(coneWrapper);
    mountDirectionalTargeting('cone');
  } else if (itemMode === 'path-direction') {
    mountDirectionalTargeting('path-direction');
  } else if (itemMode === 'player-select') {
    selectedPlayerTargetId = playerTargetIds[0] ?? null;

    const targetingLayer = document.createElement('div');
    targetingLayer.className = 'player-target-map-layer';
    targetingLayer.style.cssText = [
      'position: absolute',
      'inset: 0',
      'pointer-events: none',
      'user-select: none',
    ].join('; ');

    if (selectedPlayerTargetId === null) {
      const emptyWrapper = document.createElement('div');
      emptyWrapper.className = 'player-target-empty-panel';
      emptyWrapper.style.cssText = [
        'position: absolute',
        'top: 50%',
        'left: 50%',
        'transform: translate(-50%, -50%)',
        'display: flex',
        'flex-direction: column',
        'align-items: center',
        'gap: 12px',
        'pointer-events: auto',
      ].join('; ');

      const emptyText = document.createElement('div');
      emptyText.className = 'player-target-empty';
      emptyText.textContent = 'No valid targets';
      emptyText.style.cssText = [
        `font-family: ${BODY_FAMILY}`,
        `font-size: ${CAPTION_SIZE}`,
        'font-weight: 700',
        `color: ${SECONDARY_TEXT}`,
        'text-align: center',
      ].join('; ');
      emptyWrapper.appendChild(emptyText);

      const cancelBtn = document.createElement('button');
      cancelBtn.type = 'button';
      cancelBtn.className = 'player-target-cancel';
      cancelBtn.textContent = 'Cancel';
      cancelBtn.style.cssText = [
        `font-family: ${BODY_FAMILY}`,
        `font-size: ${CAPTION_SIZE}`,
        'font-weight: 800',
        `color: ${WHITE}`,
        'background: rgba(255,255,255,0.12)',
        'border: 2px solid rgba(255,255,255,0.24)',
        `border-radius: ${RADIUS_LG}`,
        'padding: 9px 22px',
        'cursor: pointer',
      ].join('; ');
      cancelBtn.addEventListener('click', cancelChoice);
      emptyWrapper.appendChild(cancelBtn);
      targetingLayer.appendChild(emptyWrapper);
    } else {
      playerTargetIndicator = document.createElement('div');
      playerTargetIndicator.className = 'player-target-map-indicator';
      playerTargetIndicator.style.cssText = [
        'position: absolute',
        'left: 50%',
        'top: 50%',
        'width: 118px',
        'height: 118px',
        'margin-left: -59px',
        'margin-top: -88px',
        `border-radius: ${RADIUS_CIRCLE}`,
        `border: 5px solid rgba(255, 218, 92, 0.96)`,
        'box-shadow: 0 0 24px rgba(232,160,32,0.75), inset 0 0 20px rgba(232,160,32,0.2)',
        'box-sizing: border-box',
      ].join('; ');

      const crosshair = document.createElement('div');
      crosshair.className = 'player-target-map-crosshair';
      crosshair.style.cssText = [
        'position: absolute',
        'inset: 18px',
        `border-radius: ${RADIUS_CIRCLE}`,
        'border: 3px solid rgba(255, 240, 172, 0.82)',
        'box-sizing: border-box',
      ].join('; ');
      playerTargetIndicator.appendChild(crosshair);

      playerTargetLabel = document.createElement('div');
      playerTargetLabel.className = 'player-target-map-label';
      playerTargetLabel.style.cssText = [
        'position: absolute',
        'left: 50%',
        'top: calc(100% + 8px)',
        'transform: translateX(-50%)',
        `background: ${PANEL_BG_ALPHA}`,
        `border: 2px solid ${AMBER}`,
        `border-radius: 8px`,
        'padding: 5px 12px',
        `font-family: ${BODY_FAMILY}`,
        `font-size: ${CAPTION_SIZE}`,
        'font-weight: 900',
        `color: ${WHITE}`,
        'white-space: nowrap',
        'text-shadow: 0 2px 0 rgba(0,0,0,0.72)',
      ].join('; ');
      playerTargetIndicator.appendChild(playerTargetLabel);
      targetingLayer.appendChild(playerTargetIndicator);

      playerTargetingDispose =
        payload.startItemPlayerTargeting?.({
          targetPlayerIds: playerTargetIds,
          selectedTargetId: selectedPlayerTargetId,
          onSelectTarget: selectPlayerTarget,
          onConfirmTarget: confirmPlayerTarget,
          onCancel: cancelChoice,
        }) ?? null;

      selectPlayerTarget(selectedPlayerTargetId);
      startPlayerTargetIndicatorUpdates();
    }

    root.appendChild(targetingLayer);
  } else if (itemMode === 'self-cast') {
    if (itemId === 'gravity_well') {
      rangePreviewDispose =
        payload.startItemRangePreview?.({
          itemId,
          centerPlayerId: localPlayerId,
          radius: GRAVITY_WELL_RANGE,
        }) ?? null;
    }

    // Confirmation panel with Confirm + Cancel buttons
    const confirmPanel = document.createElement('div');
    confirmPanel.className = 'self-cast-confirm-panel';
    confirmPanel.style.cssText = [
      'position: absolute',
      'top: 50%',
      'left: 50%',
      'transform: translate(-50%, -50%)',
      `background: ${PANEL_BG_ALPHA}`,
      `border-radius: ${RADIUS_LG}`,
      'padding: 24px 32px',
      'display: flex',
      'flex-direction: column',
      'align-items: center',
      'gap: 16px',
      'pointer-events: auto',
    ].join('; ');

    const confirmBtn = createButton({
      gsapRegistry,
      variant: 'primary',
      label: 'Confirm',
      onClick: () => {
        playLocalItemConfirmSfx();
        handleChoice({ type: 'use_item', itemId });
      },
    });
    buttonHandles.push(confirmBtn);
    confirmPanel.appendChild(confirmBtn.element);

    const cancelBtn = createButton({
      gsapRegistry,
      variant: 'ghost',
      label: 'Cancel',
      onClick: cancelChoice,
    });
    buttonHandles.push(cancelBtn);
    confirmPanel.appendChild(cancelBtn.element);

    root.appendChild(confirmPanel);
  }

  // ── ESC key handler — named function for deregistration ──────────────────

  function onEscKeydown(e: KeyboardEvent): void {
    if (disposed) return;
    if (e.key === 'Escape') {
      if (choiceMade || actionControlsLocked) return;
      cancelChoice();
      return;
    }
    if (choiceMade) return;
    if (itemMode === 'player-select' && payload.startItemPlayerTargeting == null) {
      if (e.code === 'ArrowLeft' || e.code === 'KeyA') {
        e.preventDefault();
        cyclePlayerTarget(-1);
      } else if (e.code === 'ArrowRight' || e.code === 'KeyD') {
        e.preventDefault();
        cyclePlayerTarget(1);
      } else if (e.code === 'Enter' || e.code === 'Space') {
        if (document.activeElement instanceof HTMLButtonElement) return;
        e.preventDefault();
        if (selectedPlayerTargetId !== null) confirmPlayerTarget(selectedPlayerTargetId);
      }
      return;
    }
    if (
      (itemMode === 'skillshot' || itemMode === 'cone') &&
      payload.startItemDirectionalTargeting == null
    ) {
      if (e.code === 'ArrowLeft' || e.code === 'KeyA') {
        e.preventDefault();
        aimDirection({ dx: -1, dy: 0 });
      } else if (e.code === 'ArrowRight' || e.code === 'KeyD') {
        e.preventDefault();
        aimDirection({ dx: 1, dy: 0 });
      } else if (e.code === 'ArrowUp' || e.code === 'KeyW') {
        e.preventDefault();
        aimDirection({ dx: 0, dy: -1 });
      } else if (e.code === 'ArrowDown' || e.code === 'KeyS') {
        e.preventDefault();
        aimDirection({ dx: 0, dy: 1 });
      } else if (e.code === 'Enter' || e.code === 'Space') {
        if (document.activeElement instanceof HTMLButtonElement) return;
        e.preventDefault();
        confirmDirectionTarget();
      }
    }
  }

  document.addEventListener('keydown', onEscKeydown);

  unsubscribe = payload.subscribe((msg: ServerMessage) => {
    if (disposed) return;

    if (msg.type === 'state_sync') {
      const nextState = deserializeGameState(msg.state);
      boardTimer.updateFromGameState(nextState);
      if (!choiceMade) return;
      if (isLocalItemPrompt(msg)) return;

      const nextPayload: BoardMountPayload = {
        ...payload,
        state: nextState,
      };
      const target = payload.itemReturnTarget?.(nextPayload.state) ?? null;
      dispose();
      if (target != null) {
        target.beforeNavigate?.();
        router.navigate(target.screenId, target.payload ?? nextPayload);
      } else {
        router.navigate('board-hud', nextPayload);
      }
      return;
    }

    if (!choiceMade) return;

    if (msg.type === 'notification' && isItemChoiceRejection(msg.text)) {
      console.warn(`[item-targeting] server rejected item choice: ${msg.text}`);
      choiceMade = false;
      root.dataset.pending = 'false';
      rejectionMessage.textContent = formatItemChoiceRejection(msg.text);
      rejectionMessage.style.display = 'block';
      applyActionLockState();
    }
  });

  // ── Mount to host ─────────────────────────────────────────────────────────

  host.appendChild(root);
  unsubscribeActionLock =
    payload.subscribeActionControlLock?.((locked) => {
      if (disposed || actionControlsLocked === locked) return;
      actionControlsLocked = locked;
      applyActionLockState();
    }) ?? null;
  applyActionLockState();

  // ── dispose ───────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    document.removeEventListener('keydown', onEscKeydown);
    unsubscribe?.();
    unsubscribe = null;
    unsubscribeActionLock?.();
    unsubscribeActionLock = null;
    playerTargetingDispose?.();
    playerTargetingDispose = null;
    directionTargetingDispose?.();
    directionTargetingDispose = null;
    rangePreviewDispose?.();
    rangePreviewDispose = null;
    stopPlayerTargetIndicatorUpdates();
    boardTimer.dispose();

    for (const handle of buttonHandles) {
      handle.dispose();
    }

    payload.clearItemInHandPreview?.();

    if (root.parentNode) {
      root.parentNode.removeChild(root);
    }
  }

  function cancelChoice(): void {
    if (choiceMade || actionControlsLocked) return;
    if (payload.itemCancelMode === 'return') {
      const target = payload.itemReturnTarget?.(state) ?? null;
      dispose();
      if (target != null) {
        target.beforeNavigate?.();
        router.navigate(target.screenId, target.payload ?? payload);
      } else {
        router.navigate('board-hud', payload);
      }
      return;
    }
    dispose();
    router.navigate('board-hud', payload);
  }

  function handleChoice(msg: ClientMessage): void {
    if (disposed || choiceMade || actionControlsLocked || !canUseItem) return;
    choiceMade = true;
    root.dataset.pending = 'true';
    rejectionMessage.style.display = 'none';
    applyActionLockState();
    payload.send(msg);
  }

  function applyActionLockState(): void {
    const locked = actionControlsLocked || choiceMade;
    root.dataset.actionLocked = locked ? 'true' : 'false';
    for (const button of Array.from(root.querySelectorAll('button'))) {
      button.disabled = locked;
      button.style.pointerEvents = locked ? 'none' : 'auto';
      button.style.opacity = locked ? '0.68' : '';
      button.toggleAttribute('data-action-locked', locked);
    }
  }

  function playLocalItemConfirmSfx(): void {
    payload.playSfx?.('sfx-ui-button-click');
  }

  function mountDirectionalTargeting(mode: 'skillshot' | 'cone' | 'path-direction'): void {
    directionTargetLabel = document.createElement('div');
    directionTargetLabel.className = 'direction-target-map-label';
    directionTargetLabel.style.cssText = [
      'position: absolute',
      'left: 50%',
      'top: 50%',
      'transform: translate(-50%, -122px)',
      `background: ${PANEL_BG_ALPHA}`,
      `border: 2px solid ${AMBER}`,
      `border-radius: 8px`,
      'padding: 5px 12px',
      `font-family: ${BODY_FAMILY}`,
      `font-size: ${CAPTION_SIZE}`,
      'font-weight: 900',
      `color: ${WHITE}`,
      'white-space: nowrap',
      'text-shadow: 0 2px 0 rgba(0,0,0,0.72)',
      'pointer-events: none',
    ].join('; ');
    root.appendChild(directionTargetLabel);
    updateDirectionLabel(mode);
    directionTargetingDispose =
      payload.startItemDirectionalTargeting?.({
        itemId,
        selectionMode: mode === 'path-direction' ? 'path-arrow' : 'free-aim',
        selectedDirection,
        selectedTargetNodeId,
        onAimDirection: (direction, targetNodeId = null) => {
          selectedTargetNodeId = targetNodeId;
          aimDirection(direction);
          updateDirectionLabel(mode);
        },
        onConfirmDirection: (direction, targetNodeId = null) => {
          selectedTargetNodeId = targetNodeId;
          aimDirection(direction);
          confirmDirectionTarget();
        },
        onCancel: cancelChoice,
      }) ?? null;
  }

  function aimDirection(direction: { dx: number; dy: number }): void {
    const magnitude = Math.hypot(direction.dx, direction.dy);
    selectedDirection =
      Number.isFinite(magnitude) && magnitude > 0.001
        ? { dx: direction.dx / magnitude, dy: direction.dy / magnitude }
        : { dx: 1, dy: 0 };
    updateDirectionLabel(
      itemMode === 'cone' ? 'cone' : itemMode === 'path-direction' ? 'path-direction' : 'skillshot',
    );
  }

  function updateDirectionLabel(mode: 'skillshot' | 'cone' | 'path-direction'): void {
    if (directionTargetLabel == null) return;
    const label =
      Math.abs(selectedDirection.dx) >= Math.abs(selectedDirection.dy)
        ? selectedDirection.dx < 0
          ? 'Aiming left'
          : 'Aiming right'
        : selectedDirection.dy < 0
          ? 'Aiming up'
          : 'Aiming down';
    if (mode === 'path-direction') {
      directionTargetLabel.textContent = `${label.replace('Aiming', 'Selected')} arrow`;
      return;
    }
    directionTargetLabel.textContent = mode === 'cone' ? `${label} cone` : label;
  }

  function confirmDirectionTarget(): void {
    playLocalItemConfirmSfx();
    handleChoice({
      type: 'use_item',
      itemId,
      direction: selectedDirection,
      ...(itemId === 'thunder_fist' && selectedTargetNodeId != null
        ? { targetNodeId: selectedTargetNodeId }
        : {}),
    });
  }

  function cyclePlayerTarget(direction: -1 | 1): void {
    if (playerTargetIds.length === 0) return;
    const currentIndex =
      selectedPlayerTargetId == null ? -1 : playerTargetIds.indexOf(selectedPlayerTargetId);
    const nextIndex =
      currentIndex < 0
        ? 0
        : (currentIndex + direction + playerTargetIds.length) % playerTargetIds.length;
    selectPlayerTarget(playerTargetIds[nextIndex]!);
  }

  function selectPlayerTarget(playerId: string): void {
    if (!playerTargetIds.includes(playerId)) return;
    selectedPlayerTargetId = playerId;
    if (playerTargetLabel != null) {
      playerTargetLabel.textContent = playerName(state, playerId);
    }
    updatePlayerTargetIndicator();
  }

  function confirmPlayerTarget(playerId: string): void {
    if (!playerTargetIds.includes(playerId)) return;
    playLocalItemConfirmSfx();
    handleChoice({ type: 'use_item', itemId, targetId: playerId });
  }

  function startPlayerTargetIndicatorUpdates(): void {
    if (playerTargetFrameId !== null) return;
    const tick = (): void => {
      playerTargetFrameId = null;
      if (disposed || itemMode !== 'player-select') return;
      updatePlayerTargetIndicator();
      playerTargetFrameId = requestAnimationFrameSafe(tick);
    };
    playerTargetFrameId = requestAnimationFrameSafe(tick);
  }

  function stopPlayerTargetIndicatorUpdates(): void {
    if (playerTargetFrameId === null) return;
    cancelAnimationFrameSafe(playerTargetFrameId);
    playerTargetFrameId = null;
  }

  function updatePlayerTargetIndicator(): void {
    if (playerTargetIndicator == null || selectedPlayerTargetId == null) return;
    const anchor = payload.getPlayerNotificationAnchor?.(selectedPlayerTargetId) ?? null;
    if (anchor == null) {
      playerTargetIndicator.style.left = '50%';
      playerTargetIndicator.style.top = '50%';
      return;
    }
    playerTargetIndicator.style.left = `${Math.round(anchor.screenX)}px`;
    playerTargetIndicator.style.top = `${Math.round(anchor.screenY)}px`;
  }

  function isLocalItemPrompt(msg: Extract<ServerMessage, { type: 'state_sync' }>): boolean {
    const activeId = msg.state.turnOrder[msg.state.currentPlayerIndex] ?? null;
    return msg.state.phase === 'item_phase' && activeId === payload.localPlayerId;
  }

  return { element: root, dispose };
}

function isItemChoiceRejection(text: string): boolean {
  return (
    text === 'no_room' ||
    text === 'not_your_turn' ||
    text === 'item_not_owned' ||
    text === 'trapped' ||
    text === 'cursed_relic_lockout' ||
    text === 'unknown_item' ||
    text === 'invalid_target' ||
    text === 'use_item_failed' ||
    text.startsWith('wrong_phase:')
  );
}

function formatItemChoiceRejection(text: string): string {
  if (text === 'not_your_turn') return 'It is not your turn.';
  if (text === 'item_not_owned') return 'That item is no longer in your inventory.';
  if (text === 'trapped') return 'You are trapped and cannot use an item this turn.';
  if (text === 'cursed_relic_lockout') return 'Cursed Relic blocks item use.';
  if (text === 'unknown_item') return 'That item is not available.';
  if (text === 'invalid_target') return 'Choose a valid target for that item.';
  if (text.startsWith('wrong_phase:')) return 'Items can only be used at the start of your turn.';
  return 'Could not use that item. Choose another target or cancel.';
}

function appendInstructionContent(
  root: HTMLElement,
  mode: ItemTargetingPayload['itemMode'],
  itemName: string,
  onCancel: () => void,
  onConfirm: () => void,
): void {
  const modality = getInputModality();

  if (mode === 'self-cast') {
    root.textContent = `Confirm to use ${itemName}?`;
    return;
  }

  if (modality === 'gamepad') {
    if (mode === 'player-select') {
      root.appendChild(document.createTextNode('Aim at a character or use arrows, '));
    } else if (mode === 'path-direction') {
      root.appendChild(document.createTextNode('Choose a direction arrow, then press '));
    } else {
      root.appendChild(document.createTextNode('Aim and press '));
    }
    root.appendChild(createInputChip('A', '#43a047'));
    root.appendChild(document.createTextNode(' to confirm, '));
    root.appendChild(createInputChip('B', '#c62828'));
    root.appendChild(document.createTextNode(' to cancel.'));
    return;
  }

  if (mode === 'path-direction') {
    root.textContent =
      modality === 'touch'
        ? 'Tap a direction arrow, then confirm.'
        : 'Choose a direction arrow. Press Enter to confirm, Esc to cancel.';
  } else if (modality === 'touch') {
    root.textContent =
      mode === 'player-select'
        ? 'Tap a character to confirm.'
        : 'Tap a board tile to aim and confirm.';
  } else if (mode === 'player-select') {
    root.textContent = 'Click a character or use arrows. Press Enter to confirm, Esc to cancel.';
  } else {
    root.textContent =
      'Click a board tile to aim and confirm. Press Enter to confirm, Esc to cancel.';
  }

  root.style.pointerEvents = 'auto';
  root.appendChild(document.createTextNode(' '));
  if (mode === 'path-direction') {
    root.appendChild(createInlineConfirmButton(onConfirm));
    root.appendChild(document.createTextNode(' '));
  }
  root.appendChild(createInlineCancelButton(onCancel));
}

function createInputChip(label: string, color: string): HTMLElement {
  const chip = document.createElement('span');
  chip.textContent = label;
  chip.style.cssText = [
    'display: inline-flex',
    'align-items: center',
    'justify-content: center',
    'width: 28px',
    'height: 28px',
    'border-radius: 50%',
    `background: ${color}`,
    'color: #fff',
    'border: 2px solid rgba(0,0,0,0.34)',
    'box-shadow: inset 0 2px 0 rgba(255,255,255,0.34), 0 2px 0 rgba(0,0,0,0.28)',
    'font-weight: 900',
    'margin: 0 4px',
    'line-height: 1',
  ].join('; ');
  return chip;
}

function createInlineCancelButton(onCancel: () => void): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'item-targeting-inline-cancel';
  button.textContent = 'Cancel';
  button.style.cssText = [
    'margin-left: 8px',
    'padding: 6px 12px',
    `font-family: ${BODY_FAMILY}`,
    `font-size: ${CAPTION_SIZE}`,
    'font-weight: 900',
    'line-height: 1',
    `color: ${WHITE}`,
    'background: rgba(72, 42, 24, 0.86)',
    'border: 2px solid rgba(72, 42, 24, 0.96)',
    'border-radius: 8px',
    'box-shadow: 0 2px 0 rgba(0,0,0,0.28)',
    'cursor: pointer',
    'pointer-events: auto',
  ].join('; ');
  button.addEventListener('click', onCancel);
  return button;
}

function createInlineConfirmButton(onConfirm: () => void): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'item-targeting-inline-confirm';
  button.textContent = 'Confirm';
  button.style.cssText = [
    'margin-left: 8px',
    'padding: 6px 12px',
    `font-family: ${BODY_FAMILY}`,
    `font-size: ${CAPTION_SIZE}`,
    'font-weight: 900',
    'line-height: 1',
    'color: #3a2419',
    'background: rgba(255, 218, 92, 0.96)',
    'border: 2px solid rgba(72, 42, 24, 0.96)',
    'border-radius: 8px',
    'box-shadow: 0 2px 0 rgba(0,0,0,0.28)',
    'cursor: pointer',
    'pointer-events: auto',
  ].join('; ');
  button.addEventListener('click', onConfirm);
  return button;
}

function playersHas(payloadState: ItemTargetingPayload['state'], playerId: string): boolean {
  return payloadState.players.has(playerId);
}

function playerName(payloadState: ItemTargetingPayload['state'], playerId: string): string {
  return payloadState.players.get(playerId)?.name ?? playerId;
}

function requestAnimationFrameSafe(callback: FrameRequestCallback): number {
  if (typeof window.requestAnimationFrame === 'function') {
    return window.requestAnimationFrame(callback);
  }
  return window.setTimeout(() => callback(performance.now()), 16);
}

function cancelAnimationFrameSafe(frameId: number): void {
  if (typeof window.cancelAnimationFrame === 'function') {
    window.cancelAnimationFrame(frameId);
    return;
  }
  window.clearTimeout(frameId);
}
